import React from 'react'

import { useInView } from 'react-cool-inview'

import { classnames } from '@cgtrader/triangle-ui/dist/src/ssr-index'
import { bool, number, string, func, arrayOf } from 'prop-types'

import i18n from 'Pods/Shared/I18n'

import { NOOP, EMPTY_ARRAY } from 'Utils/stableDefaults'

import OneTimeOption from './OneTimeOption/OneTimeOption'
import PURCHASE_OPTION from './purchaseOptions'
import SubscriptionOption from './SubscriptionOption/SubscriptionOption'
import { subscriptionPlanShape } from '../../Utils/propTypes'

const OPTION_ORDER = [PURCHASE_OPTION.SUBSCRIPTION, PURCHASE_OPTION.ONETIME]
const ARROW_STEPS = { ArrowDown: 1, ArrowRight: 1, ArrowUp: -1, ArrowLeft: -1 }

function SubscriptionPurchase({
  planName,
  subscriptionTierBand = 'essentials',
  subscriptionBullets = EMPTY_ARRAY,
  subscriptionPricePerModel,
  subscriptionPerModelLabel,
  subscriptionFromPriceLabel,
  subscriptionDownloadsPerMonth,
  subscriptionCatalogValue,
  productPrice = null,
  productOriginalPrice = null,
  hasDiscount = false,
  selectedOption,
  onOptionChange: handleOptionChange,
  onImpression = NOOP,
  subscriptionPlan = null,
  plansRestructureEnabled = false,
}) {
  // Fires the impression once the plan selector is at least 50% visible. The
  // selector only renders when the item is in the subscription and the user has
  // no active subscription, so the event represents exactly that acquisition case.
  const { observe } = useInView({
    threshold: 0.5,
    onEnter: ({ unobserve }) => {
      unobserve()
      onImpression()
    },
  })

  const handleNavigate = (event) => {
    const step = ARROW_STEPS[event.key]
    if (!step) return

    event.preventDefault()
    const nextIndex = (OPTION_ORDER.indexOf(selectedOption) + step + OPTION_ORDER.length) % OPTION_ORDER.length
    handleOptionChange(OPTION_ORDER[nextIndex])
    event.currentTarget.parentElement.querySelectorAll('[role="radio"]')[nextIndex]?.focus()
  }

  return (
    <div
      ref={observe}
      role="radiogroup"
      aria-label={i18n.t('subscription_item_page.purchase_options_label', { ns: 'rails' })}
      className={classnames('subscription-purchase', plansRestructureEnabled && 'subscription-purchase--restructure')}
    >
      <SubscriptionOption
        selected={selectedOption === PURCHASE_OPTION.SUBSCRIPTION}
        onSelect={() => handleOptionChange(PURCHASE_OPTION.SUBSCRIPTION)}
        onNavigate={handleNavigate}
        planName={planName}
        band={subscriptionTierBand}
        bullets={subscriptionBullets}
        pricePerModel={subscriptionPricePerModel}
        perModelLabel={subscriptionPerModelLabel}
        fromLabel={subscriptionFromPriceLabel}
        downloadsPerMonth={subscriptionDownloadsPerMonth}
        catalogValue={subscriptionCatalogValue}
        plan={subscriptionPlan}
        plansRestructureEnabled={plansRestructureEnabled}
      />

      <OneTimeOption
        selected={selectedOption === PURCHASE_OPTION.ONETIME}
        onSelect={() => handleOptionChange(PURCHASE_OPTION.ONETIME)}
        onNavigate={handleNavigate}
        price={productPrice}
        originalPrice={productOriginalPrice}
        hasDiscount={hasDiscount}
        plansRestructureEnabled={plansRestructureEnabled}
      />
    </div>
  )
}

SubscriptionPurchase.propTypes = {
  planName: string.isRequired,
  subscriptionTierBand: string,
  subscriptionBullets: arrayOf(string),
  subscriptionPricePerModel: string.isRequired,
  subscriptionPerModelLabel: string.isRequired,
  subscriptionFromPriceLabel: string.isRequired,
  subscriptionDownloadsPerMonth: number.isRequired,
  subscriptionCatalogValue: string.isRequired,
  productPrice: string,
  productOriginalPrice: string,
  hasDiscount: bool,
  selectedOption: string.isRequired,
  onOptionChange: func.isRequired,
  onImpression: func,
  subscriptionPlan: subscriptionPlanShape,
  plansRestructureEnabled: bool,
}

export default SubscriptionPurchase
